"use client";

import { IconArrowNarrowRight } from "@tabler/icons-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { forwardRef, useEffect, useRef } from "react";
import CloseIcon from "@/assets/icons/close.svg";
import PercentageIcon from "@/assets/icons/percentage.svg";
import { Button } from "@/components/ui/button";
import { useMobile } from "@/lib/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { variants } from "../data";
import { useTopBanner } from "../hooks/top-banner.hook";
import useTopBannersStore from "../store/top-banners-store";
import type { TopBannerType } from "../types/top-banners.types";

export const TopBannersItem = forwardRef(
  (
    { banner, onClose }: { banner: TopBannerType; onClose: () => void },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const variant = variants[banner.variant] || variants.green;
    const { show, handleClose } = useTopBanner(banner);
    const pathname = usePathname();
    const router = useRouter();
    const { isMobile } = useMobile();
    const totalHeight = useTopBannersStore((state) => state.totalHeight);
    const prevPathnameRef = useRef(pathname);

    const scrollAmount = totalHeight + (isMobile ? 80 : 160);

    useEffect(() => {
      if (prevPathnameRef.current !== pathname) {
        const shouldScroll = sessionStorage.getItem("bannerScrollNeeded");
        if (shouldScroll === "true") {
          sessionStorage.removeItem("bannerScrollNeeded");
          setTimeout(() => {
            window.scrollBy({ top: scrollAmount, behavior: "smooth" });
          }, 50);
        }
        prevPathnameRef.current = pathname;
      }
    }, [pathname, scrollAmount]);

    const handleButtonClick = (
      e: React.MouseEvent<HTMLAnchorElement>,
      url: string,
    ) => {
      e.preventDefault();

      // Extract pathname from URL (remove domain and query params)
      const getPathnameFromUrl = (urlString: string): string => {
        try {
          // Try to parse as URL (handles full URLs with domain)
          const urlObj = new URL(urlString, window.location.origin);
          return urlObj.pathname;
        } catch {
          // If not a full URL, extract pathname from relative URL
          const pathOnly = urlString.split("?")[0];
          return pathOnly || "/";
        }
      };

      const urlPathname = getPathnameFromUrl(url);
      const isSamePage = pathname === urlPathname;

      if (isSamePage) {
        window.scrollBy({ top: scrollAmount, behavior: "smooth" });
      } else {
        sessionStorage.setItem("bannerScrollNeeded", "true");
        router.push(url);
      }
    };

    return (
      <div
        className={cn(
          variant.bg,
          "flex min-h-[52px] items-center py-2 transition-[height]",
          !show && "hidden",
        )}
        data-uuid={banner.uuid}
        ref={ref}
      >
        <div
          className={cn(
            variant.text,
            "container relative mx-auto flex flex-wrap items-center gap-x-5 gap-y-1 font-medium md:justify-center",
          )}
        >
          <div className="flex items-center gap-x-2 md:gap-x-5">
            {banner.display_icon && <PercentageIcon />}
            {banner.display_text && banner.display_button ? (
              banner.text
            ) : (
              <Link
                className="underline underline-offset-2 hover:no-underline"
                href={banner.button_url || "/"}
                onClick={(e) => handleButtonClick(e, banner.button_url || "/")}
              >
                {banner.text}
              </Link>
            )}
          </div>
          {banner.display_button && (
            <Link
              className={cn(
                variant.buttonText,
                variant.buttonBg,

                "flex h-8 items-center gap-x-2 rounded-lg px-4 py-0 font-bold text-sm md:h-9",
              )}
              href={banner.button_url || "/"}
              onClick={(e) => handleButtonClick(e, banner.button_url || "/")}
            >
              {banner.button_text || ""}
              <IconArrowNarrowRight className="size-5" stroke={1.25} />
            </Link>
          )}
          {banner.display_close && (
            <Button
              className="absolute top-1/2 right-0 min-h-9 w-9 -translate-y-1/2 p-0 text-white"
              onClick={() => {
                onClose();
                handleClose(banner.uuid, banner.text);
              }}
              variant="ghost"
            >
              <CloseIcon className={variant.text} />
            </Button>
          )}
        </div>
      </div>
    );
  },
);
